import { CommonModule } from "@angular/common";
import { Component, DestroyRef, Input, OnChanges, OnInit, SimpleChanges, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { catchError, of } from "rxjs";

import { IncarcerationHistory } from "@app/case-context/models/incarceration-history.model";
import { IncarcerationHistoryService } from "@app/case-context/services/incarceration-history.service";
import { PHMS_CASE_CONTEXT_TERMINOLOGY } from "@app/phms/phms-case-context.terminology";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";

/**
 * Incarceration-history timeline for the detail page (PHMS-IP-8). Renders the ordered facility
 * placements as a vertical timeline with a current-placement marker, plus the admit /
 * parole-eligibility / projected-release footer. Backed by the incarceration-history read model;
 * loading / empty / error states never hide the section.
 */
@Component({
  selector: "phms-case-context-incarceration-history-panel",
  standalone: true,
  imports: [CommonModule, UiLibraryModule, TerminologyIdentityDirective],
  templateUrl: "./case-context-incarceration-history-panel.component.html",
  styleUrl: "./case-context-incarceration-history-panel.component.scss",
})
export class CaseContextIncarcerationHistoryPanelComponent implements OnInit, OnChanges {
  @Input({ required: true }) incarceratedPersonId!: number;

  private readonly destroyRef = inject(DestroyRef);
  private readonly service = inject(IncarcerationHistoryService);

  readonly sectionUsage = PHMS_CASE_CONTEXT_TERMINOLOGY.incarcerationHistory;
  readonly sectionText = createTerminologyUsageSignals(this.sectionUsage);

  readonly isLoading = signal(false);
  readonly hasError = signal(false);
  readonly history = signal<IncarcerationHistory | null>(null);

  readonly terms = computed(() => this.history()?.terms ?? []);
  readonly isEmpty = computed(() => !this.hasError() && this.terms().length === 0);

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["incarceratedPersonId"] && !changes["incarceratedPersonId"].firstChange) {
      this.load();
    }
  }

  load(): void {
    if (!this.incarceratedPersonId || this.incarceratedPersonId <= 0) {
      this.history.set(null);
      return;
    }

    this.isLoading.set(true);
    this.hasError.set(false);
    this.history.set(null);

    this.service
      .getIncarcerationHistory(this.incarceratedPersonId)
      .pipe(
        catchError(() => {
          this.hasError.set(true);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.history.set(result);
        this.isLoading.set(false);
      });
  }
}
